import { IKey } from '@/common/constants/ikey'
import { coerceTreeTableBoolean } from '@/common/utils/treeTableBoolean'
import { getFieldValueFromRow } from '@/common/utils/treeTableRowKeys'

export type TreeTableSelectiveFlags = {
  /** XML IKey.TT_FORBID_SELECT_NODE — выбирать только листья. */
  forbidSelectNode?: boolean
  /** XML IKey.TT_HIDE_CHECKBOX_ON_ALL_LEVEL */
  hideCheckBoxOnAllLevel?: boolean
  /** XML IKey.TT_HIDE_CHECKBOX_LEVEL — CSV 0-based видимых уровней (Swing +1 из-за скрытого корня). */
  hideCheckBoxLevel?: string
}

function readRowFlag(row: unknown, fieldName: string): boolean | null {
  if (!row || typeof row !== 'object') return null
  const rec = row as Record<string, unknown>
  const fromField = getFieldValueFromRow(row, fieldName)
  const fromDirect = rec[fieldName]
  const fromData =
    rec.data && typeof rec.data === 'object'
      ? getFieldValueFromRow(rec.data, fieldName) ?? (rec.data as Record<string, unknown>)[fieldName]
      : undefined
  return coerceTreeTableBoolean(fromField ?? fromDirect ?? fromData)
}

/** Как Swing JJTreeTable.isSelectiveNode. */
export function isTreeTableSelectiveNode(
  node: {
    isAllowChildren?: unknown
    leaf?: unknown
    data?: unknown
    children?: unknown[]
  } | null | undefined,
  flags: TreeTableSelectiveFlags = {},
): boolean {
  if (!node) return false
  const forbidRow = readRowFlag(node, IKey.TT_FORBID_TO_SELECT_NODE) ?? readRowFlag(node.data, IKey.TT_FORBID_TO_SELECT_NODE)
  if (forbidRow != null) return !forbidRow

  const isLeaf =
    node.leaf === true ||
    (coerceTreeTableBoolean(node.isAllowChildren) !== true &&
      !(Array.isArray(node.children) && node.children.length > 0))

  if (flags.forbidSelectNode) return isLeaf
  return true
}

export function parseHideCheckBoxLevels(raw: string | null | undefined): Set<number> {
  const out = new Set<number>()
  if (raw == null || String(raw).trim() === '') return out
  for (const part of String(raw).split(',')) {
    const n = Number.parseInt(part.trim(), 10)
    if (Number.isFinite(n)) out.add(n)
  }
  return out
}

/** Как Swing isCheckBoxVisible без isEnabled таблицы. */
export function isTreeTableCheckBoxVisible(
  node: {
    isAllowChildren?: unknown
    leaf?: unknown
    data?: unknown
    children?: unknown[]
    hideCheckbox?: unknown
  } | null | undefined,
  depth: number,
  flags: TreeTableSelectiveFlags = {},
): boolean {
  if (!isTreeTableSelectiveNode(node, flags)) return false
  if (flags.hideCheckBoxOnAllLevel) return false
  const levels = parseHideCheckBoxLevels(flags.hideCheckBoxLevel)
  if (levels.has(depth)) return false
  const hideRow =
    readRowFlag(node, IKey.TT_HIDE_CHECKBOX) ??
    readRowFlag(node?.data, IKey.TT_HIDE_CHECKBOX) ??
    coerceTreeTableBoolean(node?.hideCheckbox)
  if (hideRow === true) return false
  return true
}
